<script setup lang="ts">
import CourseCard from '~/components/inscriptions/CourseCard.vue';
import SignupModal from '~/components/inscriptions/SignupModal.vue';
import PeriodMessages from '~/components/inscriptions/PeriodMessages.vue';
import type { CreneauInstance } from '#shared/types/schema';

import { useToast } from '~/composables/useToast';

const { success, error } = useToast();

type InstanceWithMsg = CreneauInstance & { global_message: string[] | null };

interface InscriptionsProps {
  data: { id: string; titre: string; description: string; show_ateliers_filter?: boolean | null };
}
const props = defineProps<InscriptionsProps>();

const config = useRuntimeConfig();
const TZ = (config.public?.tz as string) || 'Europe/Paris';

// --- Helpers dates ---
function dateKey(d: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year')!.value;
  const m = parts.find((p) => p.type === 'month')!.value;
  const da = parts.find((p) => p.type === 'day')!.value;
  return `${y}-${m}-${da}`;
}
function longDayLabel(d: Date, tz: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: tz,
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  }).format(d);
}
function yearLabel(d: Date, tz: string): number {
  return Number(
    new Intl.DateTimeFormat('fr-FR', {
      timeZone: tz,
      year: 'numeric',
    }).format(d),
  );
}
function weekdayIndex(d: Date, tz: string): number {
  const wd = new Intl.DateTimeFormat('fr-FR', { timeZone: tz, weekday: 'short' }).format(d).toLowerCase();
  const map: Record<string, number> = { lun: 0, mar: 1, mer: 2, jeu: 3, ven: 4, sam: 5, dim: 6 };
  return map[wd.replace('.', '')] ?? 0;
}
function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

// -------------------- [LIEUX FILTER] état + options --------------------
const selectedLieuIds = ref<string[]>([]); // vide = tous

const weekOffset = ref(0);
const selectedCourseType = ref<'all' | 'atelier'>('all');
// --- Data (avec query param 'lieu') ---
const { data: courses, refresh } = await useFetch<InstanceWithMsg[]>('/api/inscriptions/courses', {
  query: {
    weekOffset: weekOffset,
    courseType: selectedCourseType,
  },
  // on ne refait pas la query à chaque modif de selectedLieuIds
  // query: computed(() => ({
  // API accepte 'lieu=id1,id2'
  // lieu: selectedLieuIds.value.length ? selectedLieuIds.value.join(',') : undefined,
  // }
  // )),
  transform: (response: { data: InstanceWithMsg[] }) => response.data,
});

// -------------------- [LIEUX FILTER] options dérivées des résultats courants --------------------
const lieuxOptions = computed<{ id: string; nom: string }[]>(() => {
  const set = new Map<string, string>();
  (courses.value ?? []).forEach((c) => {
    const l: any = c.lieu;
    if (l && typeof l === 'object' && l.id) set.set(l.id, l.nom || 'Lieu');
  });
  return Array.from(set, ([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
});

// -------------------- fin ajout lieux --------------------

const courseTypeFilterEnabled = computed(() => props.data?.show_ateliers_filter !== false);
const isAtelierMode = computed(() => courseTypeFilterEnabled.value && selectedCourseType.value === 'atelier');

// --- Navigation semaines (uniquement vers l'avant) ---
function nextWeek() {
  weekOffset.value += 1;
}
function prevWeek() {
  if (weekOffset.value > 0) weekOffset.value -= 1;
}
function thisWeek() {
  weekOffset.value = 0;
}

const title = computed(() => {
  if (isAtelierMode.value) return 'Ateliers à venir';
  const now = new Date();
  const start = addDays(now, weekOffset.value * 7);
  const end = addDays(start, 6);
  const f = new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, day: '2-digit', month: 'short' });
  return `Du ${f.format(start)} au ${f.format(end)}`;
});

// --- Vue “7 jours glissants” (affichage) ---
// On filtre localement aussi (défensif) : si selectedLieuIds non vide, on ne garde que ces lieux.
const oneWeek = computed(() => {
  if (isAtelierMode.value) return [];
  const now = new Date();
  const start = addDays(now, weekOffset.value * 7);
  const all = courses.value ?? [];
  const activeFilter = new Set(selectedLieuIds.value);

  return Array.from({ length: 7 }, (_, i) => {
    const dayDate = addDays(start, i);
    const key = dateKey(dayDate, TZ);
    const label = new Intl.DateTimeFormat('fr-FR', {
      timeZone: TZ,
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
    }).format(dayDate);

    const items = all
      .filter((c) => dateKey(new Date(c.start_datetime), TZ) === key)
      .filter((c) => {
        if (!activeFilter.size) return true;
        const l: any = c.lieu;
        const id = typeof l === 'object' ? l?.id : typeof l === 'string' ? l : null;
        return id ? activeFilter.has(id) : false;
      })
      .sort((a, b) => +new Date(a.start_datetime) - +new Date(b.start_datetime));

    return { key, label, items };
  });
});

const weekStart = computed(() => {
  const now = new Date();
  return addDays(now, weekOffset.value * 7);
});
const weekEnd = computed(() => addDays(weekStart.value, 6));

const daysWithMsg = ref<string[]>([]);
const dayHasMsg = computed<Record<string, boolean>>(() => Object.fromEntries(daysWithMsg.value.map((k) => [k, true])));

const selected = ref<CreneauInstance | null>(null);
const signup = ref<InstanceType<typeof SignupModal> | null>(null);
function openSignup(c: CreneauInstance) {
  selected.value = c;
  signup.value?.reset();
  signup.value?.open();
}

const { setAttr } = useVisualEditing();

function refreshMe(statusMessage: any) {
  console.log('Refreshing after signup:', statusMessage);
  selected.value = null;
  // petit feedback utilisateur
  if (statusMessage?.ok !== false) {
    success('Inscription réussie');
  } else {
    error(statusMessage?.message || "Échec de l'inscription");
  }
  setTimeout(() => {
    refresh();
  }, 1000);
}

// --- Auto-refresh quand onglet visible ---
const visibility = useDocumentVisibility();
const { pause, resume } = useIntervalFn(() => refresh(), 15_000, { immediate: false });

if (import.meta.client) {
  onMounted(() => {
    if (visibility.value === 'visible') resume();
  });
  watch(visibility, (v) => (v === 'visible' ? resume() : pause()));
  onBeforeUnmount(pause);
}

function handleDaysChange(keys: string[]) {
  daysWithMsg.value = keys;
}
function moveToMessages() {
  const el = document.getElementById('messages-section');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}
function escapeHtml(s: string) {
  return s
    .replaceAll(/&/g, '&amp;')
    .replaceAll(/</g, '&lt;')
    .replaceAll(/>/g, '&gt;')
    .replaceAll(/"/g, '&quot;')
    .replaceAll(/'/g, '&#39;');
}

// match http(s)://... ou www.... (on évite de prendre la ponctuation finale)
const URL_RE = /((https?:\/\/|www\.)[^\s<]+)/gi;
// match email simple
const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

function autolink(text: string): string {
  const escaped = escapeHtml(text ?? '');
  const withUrls = escaped.replace(URL_RE, (m: string) => {
    const href = m.startsWith('www.') ? `http://${m}` : m;
    return `<a href="${href}" target="_blank" rel="nofollow noopener noreferrer">${m}</a>`;
  });
  const withEmails = withUrls.replace(EMAIL_RE, (m: string) => {
    return `<a href="mailto:${m}">${m}</a>`;
  });
  return withEmails;
}

const descriptionHtml = computed(() => autolink(props.data?.description ?? ''));

const filteredCourses = computed(() => {
  const all = courses.value ?? [];
  const activeFilter = new Set(selectedLieuIds.value);
  if (!activeFilter.size) return all;
  return all.filter((c) => {
    const l: any = c.lieu;
    const id = typeof l === 'object' ? l?.id : typeof l === 'string' ? l : null;
    return id ? activeFilter.has(id) : false;
  });
});

const ateliersByYear = computed(() => {
  if (!isAtelierMode.value) return [];

  const grouped = new Map<number, Map<string, { key: string; label: string; items: InstanceWithMsg[] }>>();
  const sortedCourses = filteredCourses.value
    .filter((c) => (c.creneau_type as any)?.type === 'atelier')
    .sort((a, b) => +new Date(a.start_datetime) - +new Date(b.start_datetime));

  sortedCourses.forEach((course) => {
    const start = new Date(course.start_datetime);
    const year = yearLabel(start, TZ);
    const key = dateKey(start, TZ);

    if (!grouped.has(year)) grouped.set(year, new Map());
    const yearGroup = grouped.get(year)!;

    if (!yearGroup.has(key)) {
      yearGroup.set(key, {
        key,
        label: longDayLabel(start, TZ),
        items: [],
      });
    }
    yearGroup.get(key)!.items.push(course);
  });

  return Array.from(grouped.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([year, daysMap]) => ({
      year,
      days: Array.from(daysMap.values()).map((day) => ({
        ...day,
        items: day.items.sort((a, b) => +new Date(a.start_datetime) - +new Date(b.start_datetime)),
      })),
    }));
});

watch(selectedCourseType, (type) => {
  if (type === 'atelier' && courseTypeFilterEnabled.value) {
    weekOffset.value = 0;
  }
});

watch(courseTypeFilterEnabled, (enabled) => {
  if (!enabled && selectedCourseType.value !== 'all') {
    selectedCourseType.value = 'all';
  }
});
</script>

<template>
  <div
    v-if="data.id"
    class="w-full flex flex-col gap-2 -mt-12"
    :data-directus="
      setAttr({
        collection: 'block_inscriptions',
        item: data.id,
        fields: ['titre', 'description', 'show_ateliers_filter'],
        mode: 'drawer',
      })
    "
  >
    <header class="flex flex-col items-center gap-1 mb-2">
      <h2 class="text-2xl font-bold">{{ data.titre }}</h2>
      <div
        class="text-gray-600 text-center max-w-2xl text-md p-2 [&_a]:underline [&_a]:text-ocreMedium [&_a:hover]:opacity-80"
        v-html="descriptionHtml"
        :data-directus="
          setAttr({ collection: 'block_inscriptions', item: data.id, fields: 'description', mode: 'popover' })
        "
      />
      <ToolbarInscriptions
        v-model:weekOffset="weekOffset"
        v-model:selectedLieuIds="selectedLieuIds"
        v-model:selectedCourseType="selectedCourseType"
        :course-type-filter-enabled="courseTypeFilterEnabled"
        :title="title"
        :lieux-options="lieuxOptions"
        @prev="prevWeek"
        @next="nextWeek"
        @reset="thisWeek"
      />
    </header>

    <div v-if="!isAtelierMode" class="flex flex-col items-center gap-1 mb-2">
      <PeriodMessages
        :from="weekStart"
        :to="weekEnd"
        :tz="TZ"
        @days-change="handleDaysChange"
        id="messages-section"
        style="scroll-margin-top: 150px"
      />
    </div>

    <template v-if="!isAtelierMode">
      <div class="grid gap-2">
        <section v-for="day in oneWeek" :key="day.key" class="px-4 py-2 text-center">
          <!-- :class="[day.items.length ? 'w-full' : 'w-fit']" -->
          <h3 class="font-semibold mb-2 capitalize text-xl text-center">
            <div class="relative w-fit mx-auto px-2">
              <span
                v-if="dayHasMsg[day.key] && day.items.length"
                class="absolute -top-1 -right-1 h-2.5 w-2.5 cursor-pointer"
                aria-label="Message lié à cette journée"
                title="Message lié à cette journée"
                @click="moveToMessages()"
              >
                <font-awesome
                  :icon="['fas', 'message']"
                  style="font-size: 13px"
                  class="absolute top-0 left-0 text-ocreMedium"
                />
              </span>
              {{ day.label }}
            </div>
          </h3>

          <div v-if="day.items.length" class="flex flex-wrap gap-6 justify-center">
            <CourseCard v-for="course in day.items" :key="course.id" :course="course" @signup="openSignup" />
          </div>
          <div v-else class="text-sm opacity-60 italic">Aucun cours ce jour.</div>
        </section>
      </div>
    </template>
    <template v-else>
      <div v-if="ateliersByYear.length" class="flex flex-col gap-6">
        <section v-for="group in ateliersByYear" :key="group.year" class="px-4 py-2">
          <h2 class="font-semibold text-2xl text-center mb-4">Ateliers {{ group.year }}</h2>
          <div class="flex flex-col gap-4">
            <div v-for="day in group.days" :key="day.key" class="flex flex-col gap-3">
              <h3 class="font-semibold capitalize text-lg text-center">
                {{ day.label }}
              </h3>
              <div class="flex flex-wrap gap-6 justify-center">
                <CourseCard v-for="course in day.items" :key="course.id" :course="course" @signup="openSignup" />
              </div>
            </div>
          </div>
        </section>
      </div>
      <div v-else class="text-center italic opacity-60 px-4 py-6">Aucun atelier à venir.</div>
    </template>
    <ToolbarInscriptions
      v-model:weekOffset="weekOffset"
      v-model:selectedLieuIds="selectedLieuIds"
      v-model:selectedCourseType="selectedCourseType"
      :course-type-filter-enabled="courseTypeFilterEnabled"
      :title="title"
      :lieux-options="lieuxOptions"
      compact
      @prev="prevWeek"
      @next="nextWeek"
      @reset="thisWeek"
    />

    <SignupModal ref="signup" :course="selected" @done="refreshMe" />
  </div>
</template>
